// src/pages/MockReport.jsx
import React, { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { getMockReport, retakeInterview, shuffleInterview,createNewVersion  } from "../api/mockClient"; // ✅ add new API calls

function formatTime(sec) {
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function MockReport() {
  const { interviewId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const initialReport = location.state?.report || null;
  const [report, setReport] = useState(initialReport);
  const [loading, setLoading] = useState(!initialReport);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!initialReport) {
      const fetchReport = async () => {
        try {
          const res = await getMockReport(interviewId); // GET /mock/report/:interviewId
          if (!res?.report) throw new Error("No report found");
          setReport(res.report);
        } catch (err) {
          console.error("Failed to fetch report:", err);
          setError("No report found for this interview.");
        } finally {
          setLoading(false);
        }
      };
      fetchReport();
    }
  }, [interviewId, initialReport]);

  if (loading) return <div className="p-6 text-center">Loading report...</div>;
  if (error || !report) return <div className="p-6 text-center">{error || "No report found."}</div>;

  const totalQuestions = report.totalQuestions || 0;

  // ✅ Handlers
  const handleRetake = async () => {
  try {
    const res = await retakeInterview(interviewId);
    console.log("Retake response:", res.data);

    if (res.data?.questions && res.data?.interviewId) {
      // Navigate to the page that displays the questions
      navigate(`/prep/mock/${res.data.interviewId}`, {
        state: { questions: res.data.questions },
        replace: true, // optional, avoids cluttering browser history
      });
    } else {
      alert("Invalid response from server.");
    }
  } catch (err) {
    console.error("Failed to retake interview:", err);
    alert("Could not retake interview.");
  }
};


const handleShuffle = async () => {
  try {
    const res = await shuffleInterview(interviewId);
    if (res.data?.interviewId && res.data?.questions) {
      navigate(`/mock/${res.data.interviewId}`, {
        state: { questions: res.data.questions },
      });
    } else {
      alert("Invalid shuffle response from server.");
    }
  } catch (err) {
    console.error("Failed to shuffle interview:", err);
    alert("Could not start shuffled interview.");
  }
};

const handleNewVersion = async () => {
  try {
    const res = await createNewVersion(); // POST /api/mock/versions
    const version = res.data?.version;  // ✅ new version is here

    if (version?.id) {
      alert(`✅ New version ${version.versionNumber} created! Now add questions.`);
      // Redirect user to prep/questions page
    navigate(`/prep?version=${res.data.version.id}&mode=questions`);

    } else {
      alert("Failed to create new version.");
    }
  } catch (err) {
    console.error("Failed to create new version:", err);
    alert("Could not create new version.");
  }
};

const handleExit = () => {
  navigate("/dashboard"); // or "/" depending on your route
};




  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Mock Interview Report</h2>

      {/* Overall Progress Bar */}
      <div className="mb-6">
        <div className="text-sm text-gray-500 mb-1">
          Progress: {report.answered || 0} of {totalQuestions} answered
        </div>
        <div className="w-full bg-gray-200 h-4 rounded overflow-hidden">
          <div
            className="h-4 bg-indigo-600"
            style={{ width: `${totalQuestions ? (report.answered / totalQuestions) * 100 : 0}%` }}
          />
        </div>
      </div>

      {/* Summary */}
      <div className="mb-6 p-4 border rounded-lg bg-gray-50 shadow-sm grid grid-cols-2 gap-4 text-sm">
        <div><strong>Total Questions:</strong> {totalQuestions}</div>
        <div><strong>Attempted:</strong> {report.attempted || 0}</div>
        <div><strong>Answered:</strong> {report.answered || 0}</div>
        <div><strong>Skipped:</strong> {report.skipped || 0}</div>
        <div><strong>Total Time:</strong> {formatTime(report.timeTaken || 0)}</div>
        <div><strong>Recommendations:</strong> {report.recommendations || "—"}</div>
      </div>

      {/* No answers yet */}
      <div className="text-gray-500 mb-6">No answers recorded for this interview.</div>

      {/* ✅ Action buttons */}
      <div className="flex gap-4">
        <button
          onClick={handleRetake}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700"
        >
          Retake Interview
        </button>
       
        <button
    onClick={handleNewVersion}
    className="px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700"
  > 
    Start New Version
  </button>
<button
    onClick={handleExit}
    className="px-4 py-2 bg-red-600 text-white rounded-lg shadow hover:bg-red-700"
  >
    Exit
  </button>

      </div>
    </div>
  );
}
